import React from 'react';
import {View, StyleSheet, Text, PixelRatio} from 'react-native';
import DashedLine from 'react-native-dashed-line';
import Localized from 'src/constants/AppStrings';
import Styles from '../../../Styles';
import AVTouchableOpacity from 'src/components/elements/AVTouchableOpacity';
import AVText from 'src/components/elements/AVText';
import {PreparationMethodValues} from 'src/types/PreparationMethods';
import {scanAndPayTemplate} from '../templates/scanandPayThemeHelper';

type PreparationMethodsNewProps = {
  onMethodSelected: (method: PreparationMethodValues) => void;
  selectedMethod: PreparationMethodValues;
  togoAvailable: boolean;
  dineInAvailable: boolean;
  lockerAvailable: boolean;
};
export default (props: PreparationMethodsNewProps) => {
  const prepMethod =
    props.selectedMethod === 'T'
      ? Localized.Labels.to_go
      : Localized.Labels.dine_in;

  const renderButton = (
    text: string,
    disabled: boolean,
    method: PreparationMethodValues,
    testID: string,
  ) => {
    let updatedText = text;
    if (text === prepMethod) {
      updatedText += ' (selected)';
    }
    return (
      <AVTouchableOpacity
        accessible={true}
        accessibilityRole="button"
        accessibilityLabel={updatedText}
        accessibilityHint={`You have selected '${text}' as the preparation method for your order`}
        aria-label={updatedText}
        role="button"
        style={[
          styles.btnContainer,
          scanAndPayTemplate.checkoutStyles.prepMethodBtnStyles,
          disabled && styles.disabledBtn,
        ]}
        onPress={() => {
          props.onMethodSelected(method);
        }}
        testID={testID}
        nativeID={testID}
      >
        <Text
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm6}
          style={[styles.text, disabled && styles.disabledText]}
        >
          {text}
        </Text>
      </AVTouchableOpacity>
    );
  };

  let content;

  if (props.lockerAvailable) {
    const text = Localized.Labels.locker_only;
    content = (
      <View style={styles.container}>
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm7}
          accessible={true}
          accessibilityLabel={Localized.Labels.locker_only}
          aria-label={Localized.Labels.locker_only}
          style={styles.methodText}
        >
          {text}
        </AVText>
      </View>
    );
  } else if (props.togoAvailable && props.dineInAvailable) {
    content = (
      <View style={styles.container}>
        {renderButton(
          Localized.Labels.to_go_redesign_new,
          props.selectedMethod !== PreparationMethodValues.ToGo,
          PreparationMethodValues.ToGo,
          'qa_Checkout.ToGo_Btn',
        )}
        <View style={{width: Styles.Spacing.m3}} />
        {renderButton(
          Localized.Labels.dine_in_caps,
          props.selectedMethod !== PreparationMethodValues.DineIn,
          PreparationMethodValues.DineIn,
          'qa_Checkout.DineIn_Btn',
        )}
      </View>
    );
  } else if (props.togoAvailable || props.dineInAvailable) {
    let text = Localized.Labels.to_go_only;

    if (props.dineInAvailable) {
      text = Localized.Labels.dine_in_only;
    }

    content = (
      <View style={styles.container}>
        <AVText
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm8}
          accessible={true}
          accessibilityLabel={text}
          aria-label={text}
          style={styles.methodText}
          testID="qa_Checkout.PreparationMethod_txt"
        >
          {text}
        </AVText>
      </View>
    );
  }

  if (content) {
    return (
      <View
        style={[
          styles.preparationContainer,
          scanAndPayTemplate.checkoutStyles.preparationContainerMargin,
        ]}
      >
        <Text
          maxFontSizeMultiplier={Styles.FontSizeMultiplier.maxfm11}
          accessible={true}
          accessibilityLabel={Localized.Labels.preparation_method}
          aria-label={Localized.Labels.preparation_method}
          style={styles.preparationLabel}
        >
          {Localized.Labels.preparation_method}
        </Text>
        {content}
        {scanAndPayTemplate.showDashedLinebeforeItems && (
          <DashedLine
            dashGap={PixelRatio.roundToNearestPixel(4)}
            dashLength={PixelRatio.roundToNearestPixel(6)}
            dashThickness={1}
            dashColor={Styles.Colors.PayNew.neutralHuesBase09}
            style={[styles.dashStyle, styles.bottomDashedLine]}
          />
        )}
      </View>
    );
  }

  return null;
};
const styles = StyleSheet.create({
  container: {
    flexDirection: 'row',
    justifyContent: 'flex-start',
    marginTop: Styles.Spacing.m2,
  },
  btnContainer: {
    alignSelf: 'flex-start',
    alignItems: 'center',
    justifyContent: 'center',
    backgroundColor: scanAndPayTemplate.prepMethodBtnColor,
    paddingVertical: Styles.Spacing.m2,
    borderColor: scanAndPayTemplate.primaryColor,
    borderRadius: Styles.Spacing.m4,
    overflow: 'hidden',
    minHeight: Styles.MinTouchableArea,
  },
  disabledBtn: {
    backgroundColor: Styles.white,
    borderColor: scanAndPayTemplate.primaryColor,
    borderRadius: Styles.Spacing.m4,
  },
  text: {
    color: scanAndPayTemplate.prepMethodSelectedText,
    fontWeight: 'bold',
    fontFamily: scanAndPayTemplate.textFontFamily,
    fontSize: 16,
  },
  disabledText: {
    color: scanAndPayTemplate.btnTextColor,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  methodText: {
    fontSize: Styles.Fonts.f1,
    fontFamily: scanAndPayTemplate.textFontFamily,
  },
  dashStyle: {
    height: PixelRatio.roundToNearestPixel(2),
    overflow: 'hidden',
    width: '100%',
  },
  bottomDashedLine: {
    marginTop: Styles.Spacing.m3 + Styles.Spacing.m2,
  },
  preparationContainer: {
    alignSelf: 'stretch',
  },
  preparationLabel: {
    fontSize: scanAndPayTemplate.subHeaderFontSize,
    fontWeight: '700',
    fontFamily: scanAndPayTemplate.textFontFamily,
    color: Styles.Colors.PayNew.black01,
  },
  width: {width: Styles.Spacing.m3},
});
